/**
 * Round 16 H30 -- may this session ask for app-wide reference data?
 *
 * An examiner's token is confined to /v1/examiner/* by the boundary
 * middleware (P357), so the bank identity and regime registry the app
 * reads on every load could only be refused: four 403s on every
 * examiner load, two from here and two from the operator shell, which
 * asked before /v1/me had said who was looking.
 *
 * Reads /v1/me from the cache passively -- this never fetches it; the
 * shell does -- and answers yes only once it has settled on a viewer
 * who is not an examiner. With no auth provider (a test harness) the
 * answer is yes, as it was before.
 */
import { useMe } from "shared/api/useMe";
import { useAuthStatusIfAvailable } from "shared/auth/AuthProvider";

export type OperatorReads = "yes" | "no" | "pending";

export function useOperatorReadsState(): OperatorReads {
  const status = useAuthStatusIfAvailable();
  // `useMe(false)` reads the shell's /v1/me from the cache and never
  // fetches it on its own (a bare observer with no fetcher logged an
  // error whenever something refetched it).
  const me = useMe(false);
  if (status === null) return "yes";
  if (status !== "authenticated" || !me.data) return "pending";
  return (me.data.realm_roles ?? []).includes("examiner") ? "no" : "yes";
}

export function useOperatorReads(): boolean {
  return useOperatorReadsState() === "yes";
}
